import { ProvenanceConfirmations } from "./ProvenanceConfirmations.tsx";
import { ProvenanceDocuments } from "./ProvenanceDocuments.tsx";
import { ProvenanceEntryRemoval } from "./ProvenanceEntryRemoval.tsx";
import { ProvenanceVerificationPanel } from "./ProvenanceVerification.tsx";
import { StandingPill } from "./StandingPill.tsx";
import type { EntryReadModel, VerificationIntent } from "./provenance.model.ts";

/**
 * The expansion for an entry the registry holds.
 *
 * Text, not inputs. There is no note here saying so: a rule about a form is not
 * worth telling a reader who cannot open one, and the fact that matters — what
 * withdrawing does to what is already recorded — now sits on the button that
 * does it, where only somebody who can press it is shown it.
 */
export function ProvenanceEntryRead({
  read,
  onAct,
  onUnact,
  onWithdraw,
  onWithdrawAttestation,
  onWithdrawComment,
  onOpenDocument,
  onRemoveDocument,
  onJudgeDocument,
  onUndoDocumentVerdict,
}: {
  read: EntryReadModel;
  onAct: (intent: VerificationIntent) => string | null;
  onUnact: (actId: string) => void;
  onWithdraw: () => void;
  /** Ends the reader's own attestation. Not the entry — see the list's type. */
  onWithdrawAttestation: (attestationId: string) => void;
  /** Ends the reader's own comment — OND-21. Neither the entry nor a signature. */
  onWithdrawComment: (commentId: string) => void;
  onOpenDocument: (documentId: string) => void;
  onRemoveDocument: (documentId: string) => void;
  /** Accepts or rejects ONE paper — OND-62. Not the entry, and not the others. */
  onJudgeDocument: (
    documentId: string,
    stance: "verify" | "dispute",
    reason: string,
  ) => string | null;
  onUndoDocumentVerdict: (verdictId: string) => void;
}) {
  return (
    <div className="provenance__body">
      {/* First, because it is what the strip above promised to show. */}
      <ProvenanceConfirmations
        detail={read.confirmations}
        onWithdraw={onWithdrawAttestation}
        onWithdrawComment={onWithdrawComment}
      />

      <dl className="provenance__read">
        {read.fields.map((field) => (
          <div key={field.label}>
            <dt>{field.label}</dt>
            <dd>{field.value}</dd>
          </div>
        ))}
      </dl>

      {read.ondo !== null && (
        <p className="provenance__ondo">
          <span aria-hidden="true">●</span> {read.ondo.note}
        </p>
      )}

      {/* Null when nothing on the record stands against this entry, which is
          the ordinary case — so there is nothing here to suppress and no grey
          badge claiming a finding the registry has not made. */}
      {read.standing !== null && <StandingPill standing={read.standing} />}

      <ProvenanceDocuments
        rows={read.documents}
        refusals={[]}
        editable={false}
        onAdd={() => undefined}
        onSetKind={() => undefined}
        onOpen={onOpenDocument}
        onRemove={onRemoveDocument}
        onJudge={onJudgeDocument}
        onUndoVerdict={onUndoDocumentVerdict}
      />

      <ProvenanceVerificationPanel
        panel={read.verification}
        onAct={onAct}
        onUnact={onUnact}
      />

      {/* Null when this reader may not withdraw, or the surface does not offer
          it — the model decided which, and there is no rule left here. */}
      {read.removal !== null && (
        <ProvenanceEntryRemoval removal={read.removal} onRemove={onWithdraw} />
      )}
    </div>
  );
}
